import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Lock, ShoppingBag, Search, Plus, Minus, Send, Trash2, X,
  Bike, LogOut, Clock, Banknote, CreditCard, ArrowLeft,
  CheckCircle, Bell, Radio, TrendingUp, Users, Receipt, Package, ChevronDown,
  QrCode, Building2, Printer, Volume2, Globe, DollarSign, XCircle, FileText, Star,
  Usb, Settings, TestTube, Heart, Phone, UserCheck, Wifi
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { printKitchenTicket, printClientReceipt } from '../components/PrintTicket';
import { getConnectedPrinter, connectPrinter, connectNetworkPrinter, isDirectPrintAvailable, directPrintTest, directPrintKitchen, directPrintReceipt, directPrintClosure, getActivePrinter, clearPrinter } from '../utils/escpos';
import { PLATFORM_META, CAISSE_LANGS, CAISSE_TRANSLATIONS, METHOD_LABELS, SOURCE_LABELS } from './caisse/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaissePage = () => {
  const [isAuth, setIsAuth] = useState(false);
  const [role, setRole] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [lang, setLang] = useState(() => sessionStorage.getItem('caisseLang') || 'fr');
  const [showLangMenu, setShowLangMenu] = useState(false);

  const t = (key) => CAISSE_TRANSLATIONS[lang]?.[key] || CAISSE_TRANSLATIONS.fr[key] || key;

  // Translate item name based on selected language
  const getItemName = (item) => {
    if (lang !== 'en' && item.name_translations?.[lang]) return item.name_translations[lang];
    if (lang === 'fr' && item.name_fr) return item.name_fr;
    return item.name;
  };

  // Translate category name based on selected language
  const getCatName = (cat) => {
    if (cat.name_translations?.[lang]) return cat.name_translations[lang];
    if (lang === 'fr' && cat.name_fr) return cat.name_fr;
    return cat.name;
  };

  // Data
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [servers, setServers] = useState([]);
  const [deliveryPlatforms, setDeliveryPlatforms] = useState([]);
  const [orders, setOrders] = useState([]);

  // POS State
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('all');
  const [orderType, setOrderType] = useState('dine_in');
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedServer, setSelectedServer] = useState('');
  const [platformId, setPlatformId] = useState('');
  const [platformOrderId, setPlatformOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [liveIndicator, setLiveIndicator] = useState(false);
  const [notification, setNotification] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Payment
  const [payOrder, setPayOrder] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [payMode, setPayMode] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clockTime, setClockTime] = useState('');
  const [rightPanel, setRightPanel] = useState('cart');

  // Caisse session
  const [caisseStatus, setCaisseStatus] = useState(null);
  const [caisseStatusLoaded, setCaisseStatusLoaded] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [openingCashInput, setOpeningCashInput] = useState('');
  const [closingCashInput, setClosingCashInput] = useState('');
  const [closureData, setClosureData] = useState(null);
  const [topProductNames, setTopProductNames] = useState([]);

  const eventSourceRef = useRef(null);

  // Printer panel state
  const [showPrinterPanel, setShowPrinterPanel] = useState(false);
  const [caissePrinters, setCaissePrinters] = useState([]);
  const [detectedDevices, setDetectedDevices] = useState([]);
  const [scanningUSB, setScanningUSB] = useState(false);
  const [usbPrinterRef, setUsbPrinterRef] = useState(null);
  const [usbConnected, setUsbConnected] = useState(false);
  const [networkIp, setNetworkIp] = useState('192.168.1.23');
  const [networkConnected, setNetworkConnected] = useState(false);

  // Loyalty state
  const [loyaltySearch, setLoyaltySearch] = useState('');
  const [loyaltyResults, setLoyaltyResults] = useState([]);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [selectedLoyaltyCustomer, setSelectedLoyaltyCustomer] = useState(null);
  const [loyaltySearching, setLoyaltySearching] = useState(false);
  const [loyaltyEarned, setLoyaltyEarned] = useState(null);

  // Fetch printers list
  useEffect(() => {
    if (showPrinterPanel) {
      axios.get(`${API}/admin/printer/list`).then(r => setCaissePrinters(r.data)).catch(() => {});
    }
  }, [showPrinterPanel]);

  // Scan for USB printers
  const scanUSBPrinters = async () => {
    if (!navigator.usb) { toast.error('WebUSB non supporte par ce navigateur'); return; }
    setScanningUSB(true);
    try {
      // Get already paired devices
      const paired = await navigator.usb.getDevices();
      // Request new device (shows system picker)
      try {
        const device = await navigator.usb.requestDevice({
          filters: [
            { classCode: 7 }, // Printer class
            { vendorId: 0x0483 }, { vendorId: 0x0416 }, { vendorId: 0x1504 },
            { vendorId: 0x04B8 }, { vendorId: 0x0519 },
          ]
        });
        const all = [...paired];
        if (!all.find(d => d.serialNumber === device.serialNumber)) all.push(device);
        setDetectedDevices(all.map(d => ({
          name: d.productName || 'Imprimante USB',
          manufacturer: d.manufacturerName || 'Inconnu',
          serial: d.serialNumber || '',
          vendorId: d.vendorId,
          productId: d.productId,
        })));
        toast.success(`${all.length} appareil(s) detecte(s)`);
      } catch (e) {
        // User cancelled - still show paired devices
        if (paired.length > 0) {
          setDetectedDevices(paired.map(d => ({
            name: d.productName || 'Imprimante USB',
            manufacturer: d.manufacturerName || 'Inconnu',
            serial: d.serialNumber || '',
            vendorId: d.vendorId,
            productId: d.productId,
          })));
        }
      }
    } catch (e) { toast.error('Erreur scan USB'); }
    finally { setScanningUSB(false); }
  };

  const deleteCaissePrinter = async (printerId, printerName) => {
    try {
      await axios.delete(`${API}/admin/printer/delete/${printerId}`);
      toast.success(`${printerName} supprimee`);
      const r = await axios.get(`${API}/admin/printer/list`);
      setCaissePrinters(r.data);
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const addDetectedAsNew = async (device) => {
    try {
      await axios.post(`${API}/admin/printer/add`, {
        name: device.name || 'Imprimante USB',
        brand: device.manufacturer || '',
        model: device.name || '',
        connection_type: 'usb',
        ip_address: '', port: 9100, paper_width: '80mm', role: 'all',
        auto_print_kitchen: false, auto_print_receipt: false,
        copies_kitchen: 1, copies_receipt: 1, is_default: caissePrinters.length === 0,
      });
      toast.success(`${device.name} ajoutee`);
      const r = await axios.get(`${API}/admin/printer/list`);
      setCaissePrinters(r.data);
    } catch (e) { toast.error('Erreur ajout'); }
  };

  const testCaissePrint = async () => {
    const activePrinter = usbPrinterRef || getActivePrinter();
    if (activePrinter) {
      try {
        await directPrintTest(activePrinter);
        toast.success('Test imprime !');
        return;
      } catch (e) {
        toast.error('Erreur impression: ' + (e.message || 'Inconnu'));
        if (activePrinter.mode === 'usb') {
          setUsbConnected(false);
          setUsbPrinterRef(null);
        }
      }
    } else {
      toast.error('Connectez d\'abord l\'imprimante (USB ou Reseau)');
    }
  };

  // Connect USB printer directly
  const connectUSBDirect = async () => {
    try {
      const printer = await connectPrinter();
      setUsbPrinterRef(printer);
      setUsbConnected(true);
      setNetworkConnected(false);
      await directPrintTest(printer);
      toast.success('Imprimante USB connectee et test imprime !');
    } catch (e) {
      setUsbConnected(false);
      setUsbPrinterRef(null);
      if (e.message?.includes('No device selected')) {
        toast.error('Aucun appareil selectionne');
      } else if (e.message?.includes('claim') || e.message?.includes('controle')) {
        toast.error('Erreur: ' + e.message);
      } else {
        toast.error('Erreur: ' + (e.message || 'Inconnu'));
      }
    }
  };

  // Connect network/LAN printer
  const connectNetworkDirect = async () => {
    if (!networkIp) { toast.error('Entrez l\'adresse IP'); return; }
    try {
      const printer = await connectNetworkPrinter(networkIp, 9100);
      await directPrintTest(printer);
      setNetworkConnected(true);
      setUsbConnected(false);
      setUsbPrinterRef(printer);
      toast.success(`Imprimante reseau ${networkIp} connectee ! Test imprime.`);
    } catch (e) {
      setNetworkConnected(false);
      toast.error('Erreur: ' + (e.message || 'Impossible de joindre l\'imprimante'));
    }
  };

  const disconnectPrinter = () => {
    if (usbPrinterRef) usbPrinterRef.disconnect();
    setUsbPrinterRef(null);
    setUsbConnected(false);
    setNetworkConnected(false);
    clearPrinter();
    toast.success('Deconnectee');
  };

  // Auto-reconnect to paired USB device on mount
  useEffect(() => {
    if (isDirectPrintAvailable()) {
      getConnectedPrinter().then(p => {
        if (p) { setUsbPrinterRef(p); setUsbConnected(true); }
      }).catch(() => {});
    }
  }, []);

  // Save lang preference
  useEffect(() => { sessionStorage.setItem('caisseLang', lang); }, [lang]);

  // Live clock
  useEffect(() => {
    const tick = () => setClockTime(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async () => {
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setRole(res.data.role || 'admin');
      setIsAuth(true);
      sessionStorage.setItem('caisseAuth', 'true');
      sessionStorage.setItem('caisseRole', res.data.role || 'admin');
    } catch {
      setError(t('wrong_pw'));
    }
  };

  useEffect(() => {
    const authStatus = sessionStorage.getItem('caisseAuth');
    const savedRole = sessionStorage.getItem('caisseRole');
    if (authStatus === 'true') { setIsAuth(true); if (savedRole) setRole(savedRole); }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [menuRes, catRes, tableRes, srvRes, platRes, ordRes, payRes, caisseRes, topRes] = await Promise.allSettled([
        axios.get(`${API}/admin/menu`),
        axios.get(`${API}/admin/categories`),
        axios.get(`${API}/admin/tables`),
        axios.get(`${API}/admin/servers`),
        axios.get(`${API}/admin/restaurant-settings`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/caisse/status`),
        axios.get(`${API}/admin/analytics-advanced/top-products`, { params: { limit: 15, period: '30d' } }),
      ]);
      const g = (r) => r.status === 'fulfilled' ? r.value.data : [];
      setMenuItems(g(menuRes));
      setCategories(g(catRes));
      setTables(g(tableRes));
      const srvData = g(srvRes);
      setServers(srvData);
      if (srvData.length > 0 && !selectedServer) setSelectedServer(srvData[0].id);
      const restData = platRes.status === 'fulfilled' ? platRes.value.data : {};
      setDeliveryPlatforms(restData.delivery_platforms || []);
      const ordData = g(ordRes);
      setAllOrders(ordData);
      setOrders(ordData.filter(o => !o.is_fully_paid));
      setPayments(g(payRes));
      if (caisseRes.status === 'fulfilled') { setCaisseStatus(caisseRes.value.data); setCaisseStatusLoaded(true); }
      const topData = g(topRes);
      if (topData.length > 0) setTopProductNames(topData.map(p => p.name));
    } catch {}
  }, [selectedServer]);

  useEffect(() => { if (isAuth) fetchData(); }, [isAuth, fetchData]);

  useEffect(() => {
    if (!isAuth) return;
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [isAuth, fetchData]);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880; osc.type = 'sine'; gain.gain.value = 0.3;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.stop(ctx.currentTime + 0.5);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2); gain2.connect(ctx.destination);
        osc2.frequency.value = 1100; osc2.type = 'sine'; gain2.gain.value = 0.3;
        osc2.start();
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc2.stop(ctx.currentTime + 0.5);
      }, 200);
    } catch {}
  }, [soundEnabled]);

  // SSE
  useEffect(() => {
    if (!isAuth) return;
    if (eventSourceRef.current) eventSourceRef.current.close();
    const es = new EventSource(`${API}/events/admin`);
    eventSourceRef.current = es;
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order' || data.type === 'status_update') fetchData();
        if (data.type === 'new_order') {
          setNotification(`${t('new_order_notif')} - Table ${data.table_id}`);
          playNotificationSound();
          setTimeout(() => setNotification(null), 4000);
        }
        if (data.type === 'status_update' && data.status === 'ready') {
          setNotification(`${t('ready_notif')} - Table ${data.table_id || ''}`);
          playNotificationSound();
          setTimeout(() => setNotification(null), 5000);
        }
        setLiveIndicator(true);
        setTimeout(() => setLiveIndicator(false), 2000);
      } catch {}
    };
    es.onerror = () => { es.close(); setTimeout(() => {
      if (isAuth) { eventSourceRef.current = new EventSource(`${API}/events/admin`); }
    }, 3000); };
    return () => es.close();
  }, [isAuth, fetchData, playNotificationSound, t]);

  const filtered = menuItems.filter(i => {
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCat === 'all' || i.category_id === selectedCat || i.category === selectedCat;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (selectedCat !== 'all' || search) return 0;
    const aFav = a.is_favorite ? 0 : 1;
    const bFav = b.is_favorite ? 0 : 1;
    if (aFav !== bFav) return aFav - bFav;
    const aIdx = topProductNames.indexOf(a.name);
    const bIdx = topProductNames.indexOf(b.name);
    const aRank = aIdx >= 0 ? aIdx : 999;
    const bRank = bIdx >= 0 ? bIdx : 999;
    return aRank - bRank;
  });

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) return prev.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { item_id: item.id, name: item.name, name_fr: item.name_fr, name_translations: item.name_translations, price: item.price, quantity: 1, spice_level: item.spice_level }];
    });
  };

  const updateQty = (itemId, delta) => {
    setCart(prev => prev.map(c => c.item_id === itemId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter(c => c.quantity > 0));
  };

  const total = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const formatPrice = (p) => `${p?.toLocaleString() || 0}`;

  const toggleStock = async (itemId) => {
    try {
      await axios.patch(`${API}/admin/menu/${itemId}/toggle-stock`);
      fetchData();
      toast.success(t('stock_updated'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const toggleFavorite = async (e, itemId) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/admin/menu/${itemId}/toggle-favorite`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Loyalty search
  const searchLoyaltyCustomer = async (query) => {
    setLoyaltySearch(query);
    if (query.length < 2) { setLoyaltyResults([]); return; }
    setLoyaltySearching(true);
    try {
      const { data } = await axios.get(`${API}/admin/loyalty/lookup`, { params: { search: query } });
      setLoyaltyResults(data.customers || []);
      if (data.config) setLoyaltyConfig(data.config);
    } catch { /* */ }
    finally { setLoyaltySearching(false); }
  };

  const getAvailableRewards = (customer) => {
    if (!loyaltyConfig?.rewards || !customer) return [];
    return loyaltyConfig.rewards.filter(r => customer.points >= r.threshold)
      .sort((a, b) => b.threshold - a.threshold);
  };

  const autoEarnLoyalty = async (orderId, orderTotal) => {
    if (!selectedLoyaltyCustomer) return;
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/auto-earn`, {
        order_id: orderId, order_total: orderTotal,
        customer_id: selectedLoyaltyCustomer.id,
      });
      if (data.status === 'earned') {
        setLoyaltyEarned(data);
        toast.success(`${data.customer_name}: +${data.points_earned} points (total: ${data.total_points})`);
      }
    } catch { /* silent */ }
  };

  // Today's stats
  const today = new Date().toISOString().slice(0, 10);
  const todayOrders = allOrders.filter(o => (o.created_at || '').startsWith(today));
  const todayPayments = payments.filter(p => (p.created_at || '').startsWith(today) && p.payment_status === 'paid');
  const todayRevenue = todayPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const todayCovers = todayOrders.filter(o => !o.is_takeaway).length;
  const todayTakeaway = todayOrders.filter(o => o.is_takeaway).length;
  const todayTicketMoyen = todayOrders.length > 0 ? Math.round(todayOrders.reduce((s, o) => s + (o.total || 0), 0) / todayOrders.length) : 0;
  const todayPlatformOrders = todayOrders.filter(o => o.order_source && !['dine_in', 'takeaway', 'click_collect'].includes(o.order_source)).length;

  const placeOrder = async () => {
    if (cart.length === 0) return toast.error(t('empty_cart'));
    if (orderType === 'dine_in' && !selectedTable) return toast.error(t('select_table'));
    if (!selectedServer) return toast.error(t('no_server'));

    setSending(true);
    try {
      const source = orderType === 'platform' ? platformId : orderType;
      await axios.post(`${API}/server/${selectedServer}/orders`, {
        table_id: orderType === 'dine_in' ? selectedTable : `caisse-${Date.now()}`,
        items: cart, notes, server_id: selectedServer,
        server_name: servers.find(s => s.id === selectedServer)?.name || 'Caisse',
        is_takeaway: orderType !== 'dine_in', order_source: source,
        grab_order_id: orderType === 'platform' ? platformOrderId : '',
      });
      toast.success(t('order_sent'));
      smartPrintKitchen({
        table_id: orderType === 'dine_in' ? selectedTable : (orderType === 'click_collect' ? 'C&C' : 'Emporter'),
        items: cart, notes, total, is_takeaway: orderType !== 'dine_in', order_source: source,
        server_name: servers.find(s => s.id === selectedServer)?.name || 'Caisse',
      });
      setCart([]); setNotes(''); setPlatformOrderId(''); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const recordCashPayment = async (order) => {
    try {
      const amount = order.total - (order.discount_amount || 0) - (order.paid_amount || 0);
      await axios.post(`${API}/admin/orders/${order.id}/cash-payment`, {
        amount: parseFloat(cashAmount) || amount, tip_amount: 0
      });
      toast.success(t('payment_done'));
      smartPrintReceipt({ ...order, payment_method: 'cash' });
      await autoEarnLoyalty(order.id, order.total - (order.discount_amount || 0));
      setPayOrder(null); setPayMode(null); setCashAmount('');
      setSelectedLoyaltyCustomer(null); setLoyaltySearch(''); setLoyaltyResults([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const recordCardPayment = async (order) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/payment`, { payment_method: 'card_terminal', tip_amount: 0 });
      toast.success(t('payment_card_done'));
      smartPrintReceipt({ ...order, payment_method: 'card_terminal' });
      await autoEarnLoyalty(order.id, order.total - (order.discount_amount || 0));
      setPayOrder(null); setPayMode(null);
      setSelectedLoyaltyCustomer(null); setLoyaltySearch(''); setLoyaltyResults([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const recordOtherPayment = async (order, method) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/payment`, { payment_method: method });
      toast.success(t('payment_done'));
      smartPrintReceipt({ ...order, payment_method: method });
      await autoEarnLoyalty(order.id, order.total - (order.discount_amount || 0));
      setPayOrder(null); setPayMode(null);
      setSelectedLoyaltyCustomer(null); setLoyaltySearch(''); setLoyaltyResults([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // Smart print: tries direct printer (USB/network) first, falls back to browser print
  const smartPrintKitchen = async (orderData) => {
    const printer = usbPrinterRef || getActivePrinter();
    if (printer) {
      try {
        await directPrintKitchen(printer, orderData, orderData.items);
        return;
      } catch (e) { console.warn('Direct print failed, falling back:', e.message); }
    }
    printKitchenTicket(orderData);
  };

  const smartPrintReceipt = async (orderData) => {
    const printer = usbPrinterRef || getActivePrinter();
    if (printer) {
      try {
        await directPrintReceipt(printer, orderData, orderData.items);
        return;
      } catch (e) { console.warn('Direct print failed, falling back:', e.message); }
    }
    printClientReceipt(orderData);
  };

  // Caisse open/close
  const handleOpenCaisse = async () => {
    try {
      const res = await axios.post(`${API}/admin/caisse/open`, {
        opening_cash: parseFloat(openingCashInput) || 0,
        opened_by: role,
      });
      setCaisseStatus(res.data);
      toast.success(t('caisse_opened'));
      setOpeningCashInput(''); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCloseCaisse = async () => {
    try {
      const res = await axios.post(`${API}/admin/caisse/close`, {
        closing_cash_counted: parseFloat(closingCashInput) || 0,
        closed_by: role,
      });
      setClosureData(res.data);
      toast.success(t('caisse_closed'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handlePostCloseLogout = () => {
    sessionStorage.removeItem('caisseAuth');
    sessionStorage.removeItem('caisseRole');
    setIsAuth(false);
    setPassword('');
    setCaisseStatus(null);
    setCaisseStatusLoaded(false);
    setClosureData(null);
    setShowCloseModal(false);
  };

  const printClosureTicket = (data) => {
    if (!data) return;
    const w = window.open('', '_blank', 'width=350,height=800');
    if (!w) return;
    const fp = formatPrice;
    const methodRows = Object.entries(data.by_payment_method || {}).map(([k, v]) =>
      `<tr><td>${METHOD_LABELS[k] || k}</td><td style="text-align:center">${v.count}</td><td style="text-align:right">${fp(v.total)}</td></tr>`
    ).join('');
    const serverRows = Object.entries(data.by_server || {}).map(([name, v]) =>
      `<tr><td>${name}</td><td style="text-align:center">${v.orders}</td><td style="text-align:right">${fp(v.total)}</td></tr>`
    ).join('');
    const sourceRows = Object.entries(data.by_source || {}).map(([k, v]) =>
      `<tr><td>${SOURCE_LABELS[k] || k}</td><td style="text-align:center">${v.orders}</td><td style="text-align:right">${fp(v.total)}</td></tr>`
    ).join('');

    w.document.write(`<!DOCTYPE html><html><head><style>
      @page{size:80mm auto;margin:2mm}
      body{font-family:'Courier New',monospace;font-size:11px;width:76mm;margin:0 auto;padding:3mm 0}
      .center{text-align:center} .bold{font-weight:bold} .big{font-size:15px;font-weight:bold}
      .line{border-top:1px dashed #000;margin:4px 0}
      .dline{border-top:2px solid #000;margin:6px 0}
      table{width:100%;border-collapse:collapse} td{padding:1px 0;vertical-align:top;font-size:11px}
      .section{font-weight:bold;font-size:12px;margin-top:6px;margin-bottom:2px;text-decoration:underline}
      .highlight{background:#f0f0f0;padding:2px 4px;font-weight:bold}
    </style></head><body>
      <div class="center bold big">CLOTURE DE CAISSE</div>
      <div class="center" style="font-size:10px">${new Date().toLocaleString('fr-FR')}</div>
      <div class="dline"></div>

      <div class="section">FOND DE CAISSE</div>
      <table>
        <tr><td>Ouverture</td><td style="text-align:right" class="bold">${fp(data.opening_cash)}</td></tr>
      </table>
      <div class="line"></div>

      <div class="section">ACTIVITE</div>
      <table>
        <tr><td>Commandes</td><td style="text-align:right">${data.total_orders}</td></tr>
        <tr><td>Sur place</td><td style="text-align:right">${data.dine_in_count}</td></tr>
        <tr><td>Emporter</td><td style="text-align:right">${data.takeaway_count}</td></tr>
        <tr><td>Ticket moyen</td><td style="text-align:right">${fp(data.avg_ticket)}</td></tr>
        <tr><td>Payees</td><td style="text-align:right">${data.paid_count}</td></tr>
        <tr><td>Impayees</td><td style="text-align:right">${data.unpaid_count}</td></tr>
      </table>
      <div class="line"></div>

      <div class="section">PAR MODE DE PAIEMENT</div>
      <table><tr class="bold"><td>Mode</td><td style="text-align:center">Nb</td><td style="text-align:right">Total</td></tr>${methodRows}</table>
      <div class="line"></div>

      <div class="section">PAR SOURCE</div>
      <table><tr class="bold"><td>Source</td><td style="text-align:center">Nb</td><td style="text-align:right">Total</td></tr>${sourceRows}</table>
      <div class="line"></div>

      <div class="section">PAR SERVEUR</div>
      <table><tr class="bold"><td>Serveur</td><td style="text-align:center">Nb</td><td style="text-align:right">Total</td></tr>${serverRows}</table>
      <div class="dline"></div>

      <div class="section">RECAPITULATIF CAISSE</div>
      <table>
        <tr><td class="bold">CA Total</td><td style="text-align:right" class="big">${fp(data.total_revenue)}</td></tr>
        <tr><td>Pourboires</td><td style="text-align:right">${fp(data.total_tips)}</td></tr>
        <tr><td>Remises</td><td style="text-align:right">${fp(data.total_discounts)}</td></tr>
        <tr><td>Commissions</td><td style="text-align:right">${fp(data.total_commissions)}</td></tr>
      </table>
      <div class="line"></div>

      <div class="section">CONTROLE CASH</div>
      <table>
        <tr><td>Fond de caisse</td><td style="text-align:right">${fp(data.opening_cash)}</td></tr>
        <tr><td>+ Encaissements cash</td><td style="text-align:right">${fp(data.cash_revenue)}</td></tr>
        <tr><td class="bold">= Cash attendu</td><td style="text-align:right" class="bold">${fp(data.expected_cash)}</td></tr>
        <tr><td>Cash compte</td><td style="text-align:right">${fp(data.closing_cash_counted)}</td></tr>
        <tr><td class="bold" style="color:${data.cash_difference >= 0 ? '#000' : '#d00'}">Ecart</td>
            <td style="text-align:right;font-weight:bold;color:${data.cash_difference >= 0 ? '#000' : '#d00'}">${data.cash_difference >= 0 ? '+' : ''}${fp(data.cash_difference)}</td></tr>
      </table>
      <div class="dline"></div>
      <div class="center" style="font-size:10px;margin-top:4px">--- FIN DE CLOTURE ---</div>
      <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div>
      <script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
      <\/script>
    </body></html>`);
    w.document.close();
  };

  const handleLogout = () => {
    setIsAuth(false); setPassword('');
    sessionStorage.removeItem('caisseAuth');
    sessionStorage.removeItem('caisseRole');
  };

  // No transformation needed - relative URLs resolve to current domain
  // /api/images/xxx → hibisaas.com/api/images/xxx on production

  const caisseIsOpen = caisseStatus?.status === 'open';
  const caisseIsClosed = caisseStatus?.status === 'closed';

  // ─── LOGIN SCREEN ───
  if (!isAuth) {
    return (
      <div className="h-screen w-screen bg-[#0A422D] flex items-center justify-center" data-testid="caisse-login-screen">
        <div className="bg-white rounded-3xl p-10 w-full max-w-lg shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#0A422D] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-[#D4A853]" />
            </div>
            <h1 className="text-3xl font-black text-[#2D241D]">{t('login_title')}</h1>
            <p className="text-[#6E5E52] mt-2">{t('login_sub')}</p>
          </div>
          {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4 text-center font-medium">{error}</div>}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder={t('login_placeholder')}
            className="w-full h-16 text-2xl text-center tracking-widest rounded-2xl border-2 border-[#E5DCD3] bg-[#FAF7F2] text-[#2D241D] placeholder-[#B5A99A] focus:border-[#0A422D] focus:outline-none px-6 mb-4"
            data-testid="caisse-password-input" />
          <button onClick={handleLogin}
            className="w-full h-16 bg-[#0A422D] hover:bg-[#083323] text-white text-xl font-bold rounded-2xl transition-colors active:scale-[0.98]"
            data-testid="caisse-login-btn">
            {t('login_btn')}
          </button>
        </div>
      </div>
    );
  }

  // ─── LOADING CAISSE STATUS ───
  if (!caisseStatusLoaded) {
    return (
      <div className="h-screen w-screen bg-[#0A422D] flex items-center justify-center" data-testid="caisse-loading-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4A853] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60 text-sm">{t('caisse')}...</p>
        </div>
      </div>
    );
  }

  // ─── OPENING CASH SCREEN (before accessing POS) ───
  if (!caisseIsOpen) {
    return (
      <div className="h-screen w-screen bg-[#0A422D] flex items-center justify-center" data-testid="caisse-opening-screen">
        <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
          <div className="bg-[#D4A853] p-6 text-center">
            <div className="w-16 h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-2xl font-black text-black">{t('open_caisse')}</h1>
            <p className="text-black/60 text-sm mt-1">{t('opening_cash_desc')}</p>
            <p className="text-black/40 text-xs mt-2">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          <div className="p-8 space-y-6">
            <div>
              <label className="text-sm font-semibold text-[#2D241D] mb-2 block">{t('opening_cash')} (THB)</label>
              <input type="number" value={openingCashInput} onChange={e => setOpeningCashInput(e.target.value)}
                placeholder="0"
                className="w-full h-20 text-4xl text-center rounded-2xl border-2 border-[#E5DCD3] bg-[#FAF7F2] font-black text-[#2D241D] focus:border-[#D4A853] focus:outline-none"
                autoFocus
                data-testid="caisse-opening-cash-input" />
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {[500, 1000, 2000, 3000, 5000].map(a => (
                <button key={a} onClick={() => setOpeningCashInput(String(a))}
                  className={`px-5 py-3 rounded-xl text-sm font-bold active:scale-95 transition-all ${openingCashInput === String(a) ? 'bg-[#D4A853] text-black shadow-md' : 'bg-[#F3EDE5] text-[#2D241D] hover:bg-[#E5DCD3]'}`}
                  data-testid={`caisse-quick-amount-${a}`}>
                  {formatPrice(a)}
                </button>
              ))}
            </div>
            <button onClick={handleOpenCaisse}
              className="w-full h-16 bg-[#0A422D] hover:bg-[#083323] text-white font-bold text-xl rounded-2xl active:scale-[0.98] transition-all touch-manipulation flex items-center justify-center gap-3 shadow-lg"
              data-testid="caisse-validate-opening">
              <CheckCircle className="w-6 h-6" /> {t('validate_opening')}
            </button>
            <button onClick={() => { sessionStorage.removeItem('caisseAuth'); sessionStorage.removeItem('caisseRole'); setIsAuth(false); setPassword(''); }}
              className="w-full text-center text-sm text-[#6E5E52] hover:text-[#2D241D] transition-colors"
              data-testid="caisse-logout-from-opening">
              {t('logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN CAISSE INTERFACE ───
  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-[#F5F0E8]" data-testid="caisse-page">
      {/* Notification banner */}
      {notification && (
        <div className="absolute top-0 left-0 right-0 z-50 bg-[#E16833] text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg animate-pulse">
          <Bell className="w-5 h-5" /><span className="font-bold">{notification}</span>
        </div>
      )}

      {/* Top Bar */}
      <header className="bg-[#0A422D] text-white px-3 py-1.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-black tracking-wider">{t('caisse')}</h1>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${role === 'admin' ? 'bg-[#D4A853] text-black' : 'bg-white/20'}`}>
            {role.toUpperCase()}
          </span>
          <div className={`flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full ${liveIndicator ? 'bg-red-500 text-white' : 'bg-white/10 text-white/60'}`}>
            <Radio className={`w-2.5 h-2.5 ${liveIndicator ? 'animate-pulse' : ''}`} /><span>LIVE</span>
          </div>
          {/* Caisse status indicator */}
          {caisseIsOpen && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-500 text-white font-bold">
              {formatPrice(caisseStatus?.opening_cash || 0)} {t('opening_cash')}
            </span>
          )}
        </div>

        {/* Live stats */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-0.5" data-testid="caisse-stat-revenue">
            <TrendingUp className="w-3 h-3 text-[#D4A853]" />
            <span className="text-[9px] text-white/60">{t('ca')}</span>
            <span className="text-xs font-black text-[#D4A853]">{formatPrice(todayRevenue)}</span>
          </div>
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-0.5" data-testid="caisse-stat-covers">
            <Users className="w-3 h-3 text-white/70" />
            <span className="text-[9px] text-white/60">{t('covers')}</span>
            <span className="text-xs font-bold">{todayCovers}</span>
          </div>
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-0.5" data-testid="caisse-stat-takeaway">
            <ShoppingBag className="w-3 h-3 text-white/70" />
            <span className="text-[9px] text-white/60">{t('takeaway_stat')}</span>
            <span className="text-xs font-bold">{todayTakeaway}</span>
          </div>
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-0.5" data-testid="caisse-stat-ticket">
            <Receipt className="w-3 h-3 text-white/70" />
            <span className="text-[9px] text-white/60">{t('ticket_stat')}</span>
            <span className="text-xs font-bold">{formatPrice(todayTicketMoyen)}</span>
          </div>
          {todayPlatformOrders > 0 && (
            <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-0.5" data-testid="caisse-stat-platform">
              <Bike className="w-3 h-3 text-[#E16833]" />
              <span className="text-xs font-bold text-[#E16833]">{todayPlatformOrders}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <select value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)}
            className="h-7 px-2 rounded-lg bg-white/10 border border-white/20 text-white text-xs"
            data-testid="caisse-server-select">
            {servers.map(s => <option key={s.id} value={s.id} className="text-black">{s.name}</option>)}
          </select>

          {/* Language selector */}
          <div className="relative" data-testid="caisse-lang-selector">
            <button onClick={() => setShowLangMenu(!showLangMenu)}
              className="h-7 px-2 rounded-lg bg-white/10 border border-white/20 text-white text-xs font-bold flex items-center gap-1 hover:bg-white/20 transition-colors">
              <Globe className="w-3 h-3" />
              {CAISSE_LANGS.find(l => l.code === lang)?.flag || 'FR'}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showLangMenu && (
              <div className="absolute right-0 top-8 bg-white rounded-xl shadow-2xl border border-[#E5DCD3] z-50 py-1 min-w-[140px]"
                onMouseLeave={() => setShowLangMenu(false)}>
                {CAISSE_LANGS.map(l => (
                  <button key={l.code} onClick={() => { setLang(l.code); setShowLangMenu(false); }}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#F3EDE5] ${lang === l.code ? 'bg-[#F3EDE5] font-bold text-[#0A422D]' : 'text-[#2D241D]'}`}>
                    <span className="font-mono text-xs w-5">{l.flag}</span> {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-white/50" />
            <span className="text-xs text-white font-mono font-bold" data-testid="caisse-clock">{clockTime}</span>
          </div>

          <button onClick={() => setSoundEnabled(p => !p)}
            className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? 'bg-white/10 text-white' : 'bg-red-500/20 text-red-300'}`}
            data-testid="caisse-sound-toggle" title={soundEnabled ? t('sound_on') : t('sound_off')}>
            <Volume2 className={`w-3.5 h-3.5 ${soundEnabled ? '' : 'opacity-40'}`} />
          </button>

          {/* Printer access */}
          <button onClick={() => setShowPrinterPanel(!showPrinterPanel)}
            className={`p-1.5 rounded-lg transition-colors relative ${(usbConnected || networkConnected) ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white hover:bg-white/20'}`}
            data-testid="caisse-printer-btn" title="Imprimantes">
            <Printer className="w-3.5 h-3.5" />
            {(usbConnected || networkConnected) && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full" />}
          </button>

          {/* Caisse close button */}
          {caisseIsOpen && (
            <button onClick={() => { setShowCloseModal(true); setClosureData(null); setClosingCashInput(''); }}
              className="h-7 px-3 rounded-lg bg-red-500 text-white text-[10px] font-bold flex items-center gap-1 hover:bg-red-600 active:scale-95"
              data-testid="caisse-close-btn">
              <FileText className="w-3 h-3" /> {t('close_caisse')}
            </button>
          )}

          <button onClick={handleLogout} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="caisse-logout-btn">
            <LogOut className="w-4 h-4 text-white/70 hover:text-white" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ═══ LEFT: Menu Grid (65%) ═══ */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ flex: '0 0 65%' }}>
          {/* Order type bar */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-[#E5DCD3] overflow-x-auto no-scrollbar">
            <button onClick={() => setOrderType('dine_in')}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap active:scale-95 ${orderType === 'dine_in' ? 'bg-[#0A422D] text-white shadow-lg' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="caisse-type-dine-in">
              {t('dine_in')}
            </button>
            <button onClick={() => setOrderType('takeaway')}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 whitespace-nowrap active:scale-95 ${orderType === 'takeaway' ? 'bg-[#D4A853] text-black shadow-lg' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="caisse-type-takeaway">
              <ShoppingBag className="w-4 h-4" /> {t('takeaway')}
            </button>
            <button onClick={() => setOrderType('click_collect')}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 whitespace-nowrap active:scale-95 ${orderType === 'click_collect' ? 'bg-[#E16833] text-white shadow-lg' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
              data-testid="caisse-type-click-collect">
              <Package className="w-4 h-4" /> {t('click_collect')}
            </button>
            {deliveryPlatforms.map(p => {
              const pid = typeof p === 'string' ? p : p.id;
              const meta = PLATFORM_META[pid] || { name: p.name || pid, color: '#888' };
              const active = orderType === 'platform' && platformId === pid;
              return (
                <button key={pid} onClick={() => { setOrderType('platform'); setPlatformId(pid); }}
                  className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-1.5 whitespace-nowrap active:scale-95 ${active ? 'text-white shadow-lg' : 'bg-[#F3EDE5] text-[#2D241D]'}`}
                  style={active ? { backgroundColor: meta.color } : {}}
                  data-testid={`caisse-type-${pid}`}>
                  <Bike className="w-4 h-4" /> {meta.name}
                </button>
              );
            })}
          </div>

          {/* Table + Platform selector */}
          <div className="flex items-center gap-2 px-3 py-2 bg-white/50 border-b border-[#E5DCD3]">
            {orderType === 'dine_in' && (
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                {[...tables].sort((a, b) => {
                  const numA = parseInt(String(a.number || a.id).replace(/\D/g, '')) || 0;
                  const numB = parseInt(String(b.number || b.id).replace(/\D/g, '')) || 0;
                  return numA - numB;
                }).map(t => (
                  <button key={t.id} onClick={() => setSelectedTable(t.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95 min-w-[60px] ${selectedTable === t.id ? 'bg-[#0A422D] text-white' : 'bg-white text-[#2D241D] border border-[#E5DCD3]'}`}
                    data-testid={`caisse-table-${t.id}`}>
                    T{t.number || t.id}
                  </button>
                ))}
              </div>
            )}
            {orderType === 'platform' && (
              <input value={platformOrderId} onChange={e => setPlatformOrderId(e.target.value)}
                placeholder={`N commande ${PLATFORM_META[platformId]?.name || ''}`}
                className="h-10 px-4 rounded-xl border border-[#E5DCD3] text-sm flex-1 max-w-xs"
                data-testid="caisse-platform-order-id" />
            )}
          </div>

          {/* Categories bar */}
          <div className="flex items-center gap-1.5 px-3 py-2 bg-white/30 border-b border-[#E5DCD3] overflow-x-auto no-scrollbar">
            <button onClick={() => setSelectedCat('all')}
              className={`px-3 py-2 rounded-lg text-xs font-bold flex-shrink-0 transition-all active:scale-95 ${selectedCat === 'all' ? 'bg-[#0A422D] text-white' : 'bg-white text-[#6E5E52]'}`}
              data-testid="caisse-cat-all">
              {t('all')}
            </button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setSelectedCat(c.id)}
                className={`px-3 py-2 rounded-lg text-xs font-bold flex-shrink-0 transition-all active:scale-95 ${selectedCat === c.id ? 'bg-[#0A422D] text-white' : 'bg-white text-[#6E5E52]'}`}
                data-testid={`caisse-cat-${c.id}`}>
                {getCatName(c)}
              </button>
            ))}
            <div className="ml-auto flex-shrink-0 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search')}
                className="h-9 pl-9 pr-3 rounded-lg border border-[#E5DCD3] text-sm w-48"
                data-testid="caisse-search" />
            </div>
          </div>

          {/* Items grid */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="grid grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2.5">
              {filtered.map(item => {
                const isOut = item.is_active === false;
                const isFav = item.is_favorite;
                return (
                <div key={item.id} onClick={() => !isOut && addToCart(item)}
                  onContextMenu={(e) => { e.preventDefault(); toggleStock(item.id); }}
                  className={`bg-white rounded-2xl p-2 border transition-all text-left touch-manipulation relative cursor-pointer ${isFav ? 'border-[#D4A853] ring-1 ring-[#D4A853]/30' : ''} ${isOut ? 'border-red-300 opacity-50' : !isFav ? 'border-[#E5DCD3] hover:border-[#0A422D] hover:shadow-lg active:scale-95' : 'hover:shadow-lg active:scale-95'}`}
                  data-testid={`caisse-item-${item.id}`}>
                  {isOut && (
                    <div className="absolute inset-0 bg-red-500/10 rounded-2xl flex items-center justify-center z-10 pointer-events-none">
                      <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">{t('out_of_stock')}</span>
                    </div>
                  )}
                  <button onClick={(e) => toggleFavorite(e, item.id)}
                    className={`absolute top-1.5 right-1.5 z-20 w-6 h-6 rounded-full flex items-center justify-center transition-all ${isFav ? 'bg-[#D4A853] text-white shadow-sm' : 'bg-black/20 text-white/70 hover:bg-[#D4A853] hover:text-white'}`}
                    data-testid={`caisse-fav-${item.id}`}>
                    <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-current' : ''}`} />
                  </button>
                  <div className="w-full aspect-[4/3] rounded-xl mb-1 bg-[#F3EDE5] flex items-center justify-center overflow-hidden"
                    style={item.image_url ? { backgroundImage: `url(${item.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                    {!item.image_url && <span className="text-xl font-bold text-[#D4A853]">{getItemName(item)?.charAt(0)}</span>}
                  </div>
                  <p className="text-[11px] font-semibold text-[#2D241D] line-clamp-2 leading-tight">
                    {getItemName(item)}
                    {item.spice_level > 0 && <span className="ml-1 text-red-500">{'*'.repeat(item.spice_level)}</span>}
                  </p>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-sm font-black text-[#0A422D]">{formatPrice(item.price)}</p>
                    <button onClick={(e) => { e.stopPropagation(); toggleStock(item.id); }}
                      className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold transition-colors ${isOut ? 'bg-green-500 text-white' : 'bg-red-100 text-red-600 hover:bg-red-500 hover:text-white'}`}
                      data-testid={`caisse-stock-${item.id}`}>
                      {isOut ? t('restock') : t('exhaust')}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══ RIGHT: Cart + Orders (35%) ═══ */}
        <div className="flex flex-col border-l-2 border-[#E5DCD3] bg-white" style={{ flex: '0 0 35%' }}>
          {/* Panel switcher */}
          <div className="flex border-b-2 border-[#E5DCD3]">
            <button onClick={() => setRightPanel('cart')}
              className={`flex-1 py-3 text-sm font-black flex items-center justify-center gap-2 transition-colors ${rightPanel === 'cart' ? 'bg-[#FAF7F2] text-[#2D241D] border-b-2 border-[#0A422D]' : 'bg-white text-[#6E5E52]'}`}
              data-testid="caisse-panel-cart">
              <ShoppingBag className="w-4 h-4" />
              {t('cart')} ({cart.reduce((s, c) => s + c.quantity, 0)})
            </button>
            <button onClick={() => setRightPanel('orders')}
              className={`flex-1 py-3 text-sm font-black flex items-center justify-center gap-2 transition-colors relative ${rightPanel === 'orders' ? 'bg-[#FAF7F2] text-[#2D241D] border-b-2 border-[#E16833]' : 'bg-white text-[#6E5E52]'}`}
              data-testid="caisse-panel-orders">
              <Receipt className="w-4 h-4" />
              {t('orders')} ({orders.length})
              {orders.length > 0 && rightPanel !== 'orders' && (
                <span className="absolute top-1.5 right-3 w-2.5 h-2.5 bg-[#E16833] rounded-full animate-pulse" />
              )}
            </button>
          </div>

          {/* ─── CART PANEL ─── */}
          {rightPanel === 'cart' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-2 bg-[#FAF7F2] border-b border-[#E5DCD3] flex items-center justify-between">
                <span className="text-xs font-bold text-[#6E5E52]">
                  {orderType === 'dine_in' && selectedTable ? `Table ${selectedTable}` :
                   orderType === 'takeaway' ? t('takeaway') :
                   orderType === 'click_collect' ? t('click_collect') :
                   orderType === 'platform' ? (PLATFORM_META[platformId]?.name || platformId) : ''}
                </span>
                {cart.length > 0 && (
                  <button onClick={() => setCart([])} className="text-xs text-red-400 hover:text-red-600 font-bold flex items-center gap-1"
                    data-testid="caisse-clear-cart">
                    <Trash2 className="w-3.5 h-3.5" /> {t('clear')}
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
                {cart.length === 0 ? (
                  <div className="text-center py-12 text-[#6E5E52]">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm font-medium">{t('add_items')}</p>
                  </div>
                ) : cart.map(item => (
                  <div key={item.item_id} className="flex items-center gap-2 bg-[#E5DCD3] rounded-xl p-2.5" data-testid={`caisse-cart-${item.item_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#2D241D] truncate">{getItemName(item)}</p>
                      <p className="text-xs font-bold text-[#0A422D]">{formatPrice(item.price)} x {item.quantity} = {formatPrice(item.price * item.quantity)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(item.item_id, -1)}
                        className="w-10 h-10 rounded-lg bg-red-500 text-white flex items-center justify-center active:scale-90 touch-manipulation shadow-sm"
                        data-testid={`caisse-minus-${item.item_id}`}>
                        <Minus className="w-5 h-5" />
                      </button>
                      <span className="w-8 text-center text-base font-black text-[#2D241D] bg-white rounded-lg py-1">{item.quantity}</span>
                      <button onClick={() => updateQty(item.item_id, 1)}
                        className="w-10 h-10 rounded-lg bg-[#0A422D] text-white flex items-center justify-center active:scale-90 touch-manipulation shadow-sm"
                        data-testid={`caisse-plus-${item.item_id}`}>
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-3 py-2 border-t border-[#E5DCD3]">
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('notes_placeholder')}
                  className="w-full h-9 px-3 rounded-lg border border-[#E5DCD3] text-sm" data-testid="caisse-notes" />
              </div>

              <div className="px-4 py-3 bg-[#FAF7F2] border-t-2 border-[#E5DCD3]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-[#2D241D]">{t('total')}</span>
                  <span className="text-2xl font-black text-[#0A422D]" data-testid="caisse-total">{formatPrice(total)}</span>
                </div>
                <button onClick={placeOrder} disabled={sending || cart.length === 0}
                  className="w-full h-14 bg-[#0A422D] hover:bg-[#083323] disabled:opacity-40 text-white font-black text-lg rounded-2xl transition-all active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2"
                  data-testid="caisse-send-order">
                  {sending ? <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" /> :
                    <><Send className="w-5 h-5" /> {t('send_kitchen')}</>}
                </button>
              </div>
            </div>
          )}

          {/* ─── ORDERS PANEL ─── */}
          {rightPanel === 'orders' && (
            <div className="flex-1 overflow-y-auto">
              {orders.length === 0 ? (
                <div className="text-center py-16 text-[#6E5E52]">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm font-medium">{t('no_active_orders')}</p>
                </div>
              ) : orders.map(order => {
                const remaining = (order.total - (order.discount_amount || 0)) - (order.paid_amount || 0);
                const isPaid = remaining <= 0;
                const statusLabel = { received: t('received'), preparing: t('preparing'), ready: t('ready'), served: t('served') };
                const statusColor = { received: '#E9C46A', preparing: '#E16833', ready: '#2D6A4F', served: '#0A422D' };
                const sourceLabel = order.order_source && !['dine_in', 'takeaway', 'click_collect'].includes(order.order_source)
                  ? (PLATFORM_META[order.order_source]?.name || order.order_source) : null;
                return (
                  <div key={order.id} className={`border-b-2 border-[#E5DCD3] ${isPaid ? 'opacity-50' : ''}`} data-testid={`caisse-order-${order.id}`}>
                    <div className="px-4 py-3 flex items-center justify-between bg-[#FAF7F2]">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-1 rounded-lg font-bold text-white"
                          style={{ backgroundColor: statusColor[order.status] || '#888' }}>
                          {statusLabel[order.status] || order.status}
                        </span>
                        <span className="text-sm font-black text-[#2D241D]">
                          {order.is_takeaway ? (order.order_source === 'click_collect' ? 'C&C' : t('takeaway')) : `T${order.table_id}`}
                        </span>
                        {sourceLabel && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold text-white"
                            style={{ backgroundColor: PLATFORM_META[order.order_source]?.color || '#888' }}>
                            {sourceLabel}
                          </span>
                        )}
                        {order.order_number && <span className="text-[10px] text-[#6E5E52] font-mono">#{order.order_number}</span>}
                      </div>
                      <span className="text-sm font-black text-[#0A422D]">{formatPrice(order.total - (order.discount_amount || 0))}</span>
                    </div>
                    <div className="px-4 py-2 space-y-0.5">
                      {(order.items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-[#2D241D]">{item.quantity}x {item.name}</span>
                          <span className="text-[#6E5E52] font-bold">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                      ))}
                      {order.notes && <p className="text-[10px] text-[#E16833] italic mt-1">{order.notes}</p>}
                    </div>
                    <div className="px-4 py-2 bg-[#FAF7F2] flex gap-2">
                      <button onClick={() => smartPrintKitchen(order)}
                        className="h-9 px-3 rounded-lg bg-[#D4A853]/10 text-[#D4A853] hover:bg-[#D4A853] hover:text-white text-xs font-bold flex items-center gap-1 active:scale-95 transition-colors"
                        data-testid={`caisse-print-kitchen-${order.id}`}>
                        <Printer className="w-3.5 h-3.5" /> {t('kitchen')}
                      </button>
                      {isPaid && (
                        <button onClick={() => smartPrintReceipt(order)}
                          className="h-9 px-3 rounded-lg bg-[#0A422D]/10 text-[#0A422D] hover:bg-[#0A422D] hover:text-white text-xs font-bold flex items-center gap-1 active:scale-95 transition-colors"
                          data-testid={`caisse-print-receipt-${order.id}`}>
                          <Printer className="w-3.5 h-3.5" /> {t('ticket')}
                        </button>
                      )}
                      {remaining > 0 && (
                        <button onClick={() => { setPayOrder(order); setPayMode(null); setCashAmount(String(Math.round(remaining))); }}
                          className="flex-1 h-9 rounded-lg bg-[#0A422D] text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 touch-manipulation"
                          data-testid={`caisse-pay-${order.id}`}>
                          <Banknote className="w-3.5 h-3.5" /> {t('collect')} ({formatPrice(remaining)})
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ═══ PAYMENT OVERLAY ═══ */}
      {payOrder && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => { setPayOrder(null); setPayMode(null); setSelectedLoyaltyCustomer(null); setLoyaltySearch(''); setLoyaltyResults([]); }}>
          <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="caisse-payment-overlay">
            <div className="bg-[#0A422D] text-white p-4 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">{t('payment_title')}</h3>
                <p className="text-sm opacity-75">
                  {payOrder.is_takeaway ? (payOrder.order_source === 'click_collect' ? t('click_collect') : t('takeaway')) : `Table ${payOrder.table_id}`}
                  {payOrder.order_number ? ` | ${payOrder.order_number}` : ''}
                </p>
              </div>
              <button onClick={() => { setPayOrder(null); setPayMode(null); }} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-[#E5DCD3]">
              <div className="flex justify-between items-center">
                <span className="text-[#6E5E52]">{t('total')}</span>
                <span className="text-2xl font-bold text-[#2D241D]">{formatPrice(payOrder.total - (payOrder.discount_amount || 0))}</span>
              </div>
              {payOrder.paid_amount > 0 && (
                <div className="flex justify-between items-center mt-1">
                  <span className="text-sm text-[#6E5E52]">{t('already_paid')}</span>
                  <span className="text-sm text-[#2D6A4F]">-{formatPrice(payOrder.paid_amount)}</span>
                </div>
              )}
            </div>

            {!payMode && (
              <div className="p-4 space-y-3">
                {/* Loyalty customer lookup */}
                <div className="bg-[#FAF7F2] rounded-2xl p-3 space-y-2" data-testid="caisse-loyalty-section">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#2D241D]">
                    <Heart className="w-4 h-4 text-[#E16833]" /> Fidelite
                  </div>
                  {!selectedLoyaltyCustomer ? (
                    <>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
                        <input type="text" value={loyaltySearch}
                          onChange={(e) => searchLoyaltyCustomer(e.target.value)}
                          placeholder="Telephone ou nom du client..."
                          className="w-full h-10 pl-10 pr-3 rounded-xl border border-[#E5DCD3] text-sm bg-white text-[#2D241D]"
                          data-testid="caisse-loyalty-search" />
                        {loyaltySearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#D4A853] border-t-transparent rounded-full animate-spin" />}
                      </div>
                      {loyaltyResults.length > 0 && (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {loyaltyResults.map(c => (
                            <button key={c.id} onClick={() => { setSelectedLoyaltyCustomer(c); setLoyaltySearch(''); setLoyaltyResults([]); }}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-[#E5DCD3] hover:border-[#D4A853] text-left transition-colors"
                              data-testid={`loyalty-result-${c.id}`}>
                              <div>
                                <p className="text-sm font-medium text-[#2D241D]">{c.name}</p>
                                <p className="text-[10px] text-[#6E5E52]">{c.phone}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-[#D4A853]">{c.points} pts</p>
                                <p className="text-[10px] text-[#6E5E52]">{c.total_visits} visites</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {loyaltySearch.length >= 2 && loyaltyResults.length === 0 && !loyaltySearching && (
                        <p className="text-xs text-[#6E5E52] text-center py-1">Aucun client trouve</p>
                      )}
                    </>
                  ) : (
                    <div className="bg-white rounded-xl p-3 border border-[#D4A853]" data-testid="caisse-loyalty-selected">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-5 h-5 text-[#2D6A4F]" />
                          <div>
                            <p className="text-sm font-bold text-[#2D241D]">{selectedLoyaltyCustomer.name}</p>
                            <p className="text-[10px] text-[#6E5E52]">{selectedLoyaltyCustomer.phone}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black text-[#D4A853]">{selectedLoyaltyCustomer.points} pts</p>
                          <button onClick={() => { setSelectedLoyaltyCustomer(null); setLoyaltySearch(''); }}
                            className="text-[10px] text-red-400 hover:text-red-600"
                            data-testid="caisse-loyalty-remove">Retirer</button>
                        </div>
                      </div>
                      {getAvailableRewards(selectedLoyaltyCustomer).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[#E5DCD3]">
                          <p className="text-[10px] font-bold text-[#2D6A4F] mb-1">Recompenses disponibles :</p>
                          <div className="flex gap-1 flex-wrap">
                            {getAvailableRewards(selectedLoyaltyCustomer).slice(0, 3).map((r, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] font-bold">
                                {r.label} ({r.threshold} pts)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button onClick={() => setPayMode('cash')}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#2D6A4F] text-white font-semibold text-sm active:scale-[0.98] touch-manipulation"
                  data-testid="caisse-checkout-cash">
                  <Banknote className="w-6 h-6" /> <span className="flex-1 text-left">{t('cash')}</span>
                  <span className="opacity-75">{formatPrice((payOrder.total - (payOrder.discount_amount || 0)) - (payOrder.paid_amount || 0))}</span>
                </button>
                <button onClick={() => recordCardPayment(payOrder)}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#6366F1] text-white font-semibold text-sm active:scale-[0.98] touch-manipulation"
                  data-testid="caisse-checkout-cb">
                  <CreditCard className="w-6 h-6" /> <span className="flex-1 text-left">{t('card')}</span>
                  <span className="opacity-75">{formatPrice((payOrder.total - (payOrder.discount_amount || 0)) - (payOrder.paid_amount || 0))}</span>
                </button>
                <button onClick={() => setPayMode('promptpay')}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#1B3A4B] text-white font-semibold text-sm active:scale-[0.98] touch-manipulation"
                  data-testid="caisse-checkout-promptpay">
                  <QrCode className="w-6 h-6" /> <span className="flex-1 text-left">{t('promptpay')}</span>
                  <span className="opacity-75">{formatPrice((payOrder.total - (payOrder.discount_amount || 0)) - (payOrder.paid_amount || 0))}</span>
                </button>
                <button onClick={() => recordOtherPayment(payOrder, 'counter')}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#0A422D] text-white font-semibold text-sm active:scale-[0.98] touch-manipulation"
                  data-testid="caisse-checkout-counter">
                  <Building2 className="w-6 h-6" /> <span className="flex-1 text-left">{t('counter_pay')}</span>
                </button>
              </div>
            )}

            {payMode === 'cash' && (() => {
              const rem = (payOrder.total - (payOrder.discount_amount || 0)) - (payOrder.paid_amount || 0);
              return (
                <div className="p-4 space-y-3">
                  <button onClick={() => setPayMode(null)} className="text-xs text-[#6E5E52] hover:underline flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> {t('back')}
                  </button>
                  <div>
                    <label className="text-sm font-medium text-[#2D241D] mb-1 block">{t('amount_received')}</label>
                    <input type="number" value={cashAmount} onChange={e => setCashAmount(e.target.value)}
                      placeholder={String(rem)}
                      className="w-full h-14 text-2xl text-center rounded-2xl border-2 border-[#E5DCD3]"
                      data-testid="caisse-cash-amount" />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[100, 200, 500, 1000].map(a => (
                      <button key={a} onClick={() => setCashAmount(String(a))}
                        className="px-3 py-1.5 rounded-lg bg-[#F3EDE5] text-sm font-medium text-[#2D241D] hover:bg-[#E5DCD3] active:scale-95">
                        {a}
                      </button>
                    ))}
                    <button onClick={() => setCashAmount(String(rem))}
                      className="px-3 py-1.5 rounded-lg bg-[#D4A853]/20 text-sm font-medium text-[#D4A853] hover:bg-[#D4A853]/30 active:scale-95">
                      {t('exact')}
                    </button>
                  </div>
                  {cashAmount && parseFloat(cashAmount) > rem && (
                    <div className="bg-[#FFF8E1] rounded-xl p-3 text-center">
                      <p className="text-sm text-[#D4A853] font-semibold">{t('change')} : {formatPrice(parseFloat(cashAmount) - rem)}</p>
                    </div>
                  )}
                  <button onClick={() => recordCashPayment(payOrder)} disabled={!cashAmount}
                    className="w-full h-14 bg-[#2D6A4F] hover:bg-[#1B4332] disabled:opacity-40 text-white font-bold text-lg rounded-2xl active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2"
                    data-testid="caisse-confirm-cash">
                    <CheckCircle className="w-5 h-5" /> {t('validate_cash')}
                  </button>
                </div>
              );
            })()}

            {payMode === 'promptpay' && (
              <div className="p-4 space-y-3 text-center">
                <button onClick={() => setPayMode(null)} className="text-xs text-[#6E5E52] hover:underline flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> {t('back')}
                </button>
                <p className="text-[#6E5E52] text-sm">
                  {t('total')}: <span className="text-[#D4A853] font-bold text-lg">{formatPrice((payOrder.total - (payOrder.discount_amount || 0)) - (payOrder.paid_amount || 0))}</span>
                </p>
                <button onClick={() => recordOtherPayment(payOrder, 'promptpay')}
                  className="w-full h-14 bg-[#1B3A4B] text-white font-bold text-lg rounded-2xl active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2"
                  data-testid="caisse-confirm-promptpay">
                  <QrCode className="w-5 h-5" /> {t('pay_received_pp')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ PRINTER PANEL ═══ */}
      {showPrinterPanel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setShowPrinterPanel(false)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="caisse-printer-panel">
            <div className="bg-[#0A422D] text-white p-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-[#D4A853]" />
                <h3 className="font-bold text-lg">Imprimantes</h3>
              </div>
              <button onClick={() => setShowPrinterPanel(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Connection status banner */}
              {(usbConnected || networkConnected) && (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-green-700 flex items-center gap-2">
                      {networkConnected ? <Wifi className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
                      {networkConnected ? `Reseau (${networkIp})` : 'USB'} connectee
                    </h4>
                    <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                  </div>
                  <p className="text-xs text-green-600">L'imprimante est prete. Tous les tickets s'imprimeront directement.</p>
                  <div className="flex gap-2">
                    <button onClick={testCaissePrint}
                      className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all"
                      data-testid="caisse-test-print">
                      <TestTube className="w-4 h-4" /> Imprimer un test
                    </button>
                    <button onClick={disconnectPrinter}
                      className="h-10 px-4 bg-red-50 hover:bg-red-100 text-red-600 font-bold text-sm rounded-xl active:scale-95 transition-all"
                      data-testid="caisse-disconnect-printer">
                      Deconnecter
                    </button>
                  </div>
                </div>
              )}

              {/* Network/LAN connection — Future option when backend is local */}
              {!networkConnected && !usbConnected && (
                <div className="rounded-2xl p-4 space-y-3 border border-[#E5DCD3] bg-[#FAF7F2]">
                  <h4 className="text-sm font-bold text-[#6E5E52] flex items-center gap-2">
                    <Wifi className="w-4 h-4" /> Impression reseau (LAN)
                  </h4>
                  <p className="text-xs text-[#6E5E52]">Votre imprimante a l'IP <strong>192.168.1.23</strong>. L'impression reseau sera disponible quand le serveur sera depoye localement.</p>
                  <div className="flex gap-2">
                    <input type="text" value={networkIp}
                      onChange={(e) => setNetworkIp(e.target.value)}
                      placeholder="192.168.1.23"
                      className="flex-1 h-10 rounded-xl border border-[#E5DCD3] px-4 text-sm font-mono text-[#2D241D] bg-white"
                      data-testid="caisse-network-ip" />
                    <button onClick={connectNetworkDirect}
                      className="h-10 px-5 bg-[#6E5E52] hover:bg-[#5A4E44] text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all"
                      data-testid="caisse-connect-network">
                      <Wifi className="w-4 h-4" /> Tester
                    </button>
                  </div>
                </div>
              )}

              {/* Direct USB connection — Primary method */}
              {!usbConnected && !networkConnected && (
                <div className="rounded-2xl p-4 space-y-3 border-2 border-[#0A422D]/30 bg-[#0A422D]/5">
                  <h4 className="text-sm font-bold text-[#0A422D] flex items-center gap-2">
                    <Usb className="w-4 h-4" /> Impression USB directe (recommande)
                  </h4>
                  <p className="text-xs text-[#6E5E52]">Imprime directement via le cable USB. Fonctionne avec votre imprimante ESC/POS.</p>
                  <button onClick={connectUSBDirect}
                    className="w-full h-12 bg-[#0A422D] hover:bg-[#083323] text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md"
                    data-testid="caisse-connect-usb-direct">
                    <Usb className="w-5 h-5" /> Connecter USB
                  </button>
                  <div className="bg-white/80 rounded-xl p-3 space-y-1.5">
                    <p className="text-[10px] font-bold text-[#E16833]">Si ca echoue ("Unable to claim") :</p>
                    <p className="text-[10px] text-[#6E5E52]">1. Redemarrez completement la tablette/POS</p>
                    <p className="text-[10px] text-[#6E5E52]">2. Debranchez le cable USB, attendez 5 sec, rebranchez</p>
                    <p className="text-[10px] text-[#6E5E52]">3. Ouvrez Chrome AVANT de brancher l'imprimante</p>
                    <p className="text-[10px] text-[#6E5E52]">4. Verifiez Parametres Android > Apps > Loyverse > Forcer l'arret</p>
                  </div>
                </div>
              )}

              {/* Scan USB — secondary */}
              <div className="bg-[#FAF7F2] rounded-2xl p-4 space-y-3">
                <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
                  <Search className="w-4 h-4 text-[#E16833]" /> Rechercher des imprimantes
                </h4>
                <p className="text-xs text-[#6E5E52]">Rechercher les imprimantes connectees par USB a cet appareil</p>
                <button onClick={scanUSBPrinters} disabled={scanningUSB}
                  className="w-full h-10 bg-[#0A422D] hover:bg-[#083323] text-white font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50"
                  data-testid="caisse-scan-usb">
                  {scanningUSB ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Recherche...</>
                  ) : (
                    <><Search className="w-4 h-4" /> Rechercher (USB)</>
                  )}
                </button>
                {detectedDevices.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-[#0A422D]">{detectedDevices.length} appareil(s) detecte(s) :</p>
                    {detectedDevices.map((d, i) => (
                      <div key={i} className="bg-white rounded-xl p-3 border border-[#E5DCD3] flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-[#2D241D]">{d.name}</p>
                          <p className="text-[10px] text-[#6E5E52]">{d.manufacturer} {d.serial && `| SN: ${d.serial}`}</p>
                        </div>
                        <button onClick={() => addDetectedAsNew(d)}
                          className="px-3 py-1.5 bg-[#D4A853] text-black text-xs font-bold rounded-lg active:scale-95"
                          data-testid={`add-detected-${i}`}>
                          + Ajouter
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Configured printers */}
              <div>
                <h4 className="text-sm font-bold text-[#2D241D] mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#6E5E52]" /> Imprimantes configurees ({caissePrinters.length})
                </h4>
                {caissePrinters.length === 0 ? (
                  <div className="text-center py-6 text-xs text-[#6E5E52]">Aucune imprimante configuree.<br/>Allez dans Admin {'\u2192'} Imprimante pour en ajouter.</div>
                ) : (
                  <div className="space-y-2">
                    {caissePrinters.map(p => (
                      <div key={p.id} className={`bg-white rounded-xl p-3 border ${p.is_default ? 'border-[#D4A853]' : 'border-[#E5DCD3]'}`}
                        data-testid={`caisse-printer-${p.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Printer className="w-4 h-4 text-[#E16833]" />
                            <span className="text-sm font-bold text-[#2D241D]">{p.name}</span>
                            {p.is_default && <Star className="w-3 h-3 text-[#D4A853] fill-current" />}
                          </div>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#0A422D]/10 text-[#0A422D] font-bold">
                            {p.role === 'kitchen' ? 'Cuisine' : p.role === 'receipt' ? 'Tickets' : p.role === 'bar' ? 'Bar' : 'Tout'}
                          </span>
                        </div>
                        <p className="text-[10px] text-[#6E5E52] mb-2">{p.brand} {p.model} | {p.connection_type === 'browser' ? 'Navigateur' : p.connection_type === 'network' ? `Reseau ${p.ip_address}` : 'USB'} | {p.paper_width}</p>
                        <div className="flex gap-2">
                          <button onClick={() => testCaissePrint(p)}
                            className="flex-1 h-8 rounded-lg bg-[#FAF7F2] hover:bg-[#E5DCD3] text-xs font-bold text-[#2D241D] flex items-center justify-center gap-1 active:scale-95 transition-colors"
                            data-testid={`caisse-test-printer-${p.id}`}>
                            <TestTube className="w-3 h-3" /> Tester
                          </button>
                          <button onClick={() => deleteCaissePrinter(p.id, p.name)}
                            className="h-8 w-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center active:scale-95 transition-colors"
                            data-testid={`caisse-delete-printer-${p.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick tip */}
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Astuce :</strong> Pour ajouter ou modifier des imprimantes, allez dans Admin {'\u2192'} Imprimante. Ici vous pouvez tester et detecter vos imprimantes connectees.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CLOSE CAISSE MODAL ═══ */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowCloseModal(false)}>
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="caisse-close-modal">
            <div className="bg-red-500 text-white p-4 rounded-t-3xl flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">{t('close_title')}</h3>
                <p className="text-sm opacity-75">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              <button onClick={() => setShowCloseModal(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!closureData ? (
              <div className="p-6 space-y-4">
                {/* Quick stats preview */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#6E5E52]">{t('ca')}</p>
                    <p className="text-xl font-black text-[#0A422D]">{formatPrice(todayRevenue)}</p>
                  </div>
                  <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#6E5E52]">{t('orders')}</p>
                    <p className="text-xl font-black text-[#2D241D]">{todayOrders.length}</p>
                  </div>
                  <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#6E5E52]">{t('covers')}</p>
                    <p className="text-xl font-black text-[#2D241D]">{todayCovers}</p>
                  </div>
                  <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#6E5E52]">{t('opening_cash')}</p>
                    <p className="text-xl font-black text-[#D4A853]">{formatPrice(caisseStatus?.opening_cash || 0)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-2 block">{t('cash_counted')}</label>
                  <input type="number" value={closingCashInput} onChange={e => setClosingCashInput(e.target.value)}
                    placeholder="0"
                    className="w-full h-14 text-2xl text-center rounded-2xl border-2 border-[#E5DCD3] bg-[#FAF7F2] font-bold text-[#2D241D]"
                    autoFocus
                    data-testid="caisse-closing-cash-input" />
                </div>

                <button onClick={handleCloseCaisse}
                  className="w-full h-14 bg-red-500 hover:bg-red-600 text-white font-bold text-lg rounded-2xl active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2"
                  data-testid="caisse-confirm-close">
                  <XCircle className="w-5 h-5" /> {t('confirm_close')}
                </button>
              </div>
            ) : (
              /* Closure result */
              <div className="p-4 space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-1" />
                  <p className="font-bold text-green-700">{t('caisse_closed')}</p>
                </div>

                {/* Summary */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6E5E52]">{t('opening_cash')}</span>
                    <span className="font-bold">{formatPrice(closureData.opening_cash)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6E5E52]">{t('ca')}</span>
                    <span className="font-black text-[#0A422D]">{formatPrice(closureData.total_revenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6E5E52]">{t('orders')}</span>
                    <span className="font-bold">{closureData.total_orders}</span>
                  </div>
                  <div className="border-t border-[#E5DCD3] pt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6E5E52]">{t('expected_cash')}</span>
                      <span className="font-bold">{formatPrice(closureData.expected_cash)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6E5E52]">{t('cash_counted')}</span>
                      <span className="font-bold">{formatPrice(closureData.closing_cash_counted)}</span>
                    </div>
                    <div className={`flex justify-between text-sm font-bold ${closureData.cash_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <span>{t('difference')}</span>
                      <span>{closureData.cash_difference >= 0 ? '+' : ''}{formatPrice(closureData.cash_difference)}</span>
                    </div>
                  </div>
                </div>

                <button onClick={() => { printClosureTicket(closureData); handlePostCloseLogout(); }}
                  className="w-full h-12 bg-[#0A422D] hover:bg-[#083323] text-white font-bold rounded-2xl active:scale-[0.98] touch-manipulation flex items-center justify-center gap-2"
                  data-testid="caisse-print-closure">
                  <Printer className="w-5 h-5" /> {t('print_and_close')}
                </button>

                <button onClick={handlePostCloseLogout}
                  className="w-full h-10 text-sm text-[#6E5E52] hover:text-[#2D241D] font-medium transition-colors"
                  data-testid="caisse-close-and-logout">
                  {t('close_without_print')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CaissePage;
